<template>
  <BaseModal class="order-modal" v-model="isOpenModal">
    <FileSelector
      v-model="showFileSelector"
      type="assets"
      subcategory="image_banner"
      @fileUploaded="fileUploaded"
      @selectedFile="fileUploaded"
    />

    <!-- Modal Header -->
    <template v-slot:header v-if="banner">
      <div class="flex items-center space-x-3">
        <div
          class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"
        >
          <span class="text-blue-600 text-lg">🖼️</span>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900">
            {{ banner.id ? "Edit Banner" : "Create New Banner" }}
          </h3>
          <p class="text-sm text-gray-500" v-if="banner.title">
            {{ banner.title }}
          </p>
        </div>
      </div>
    </template>

    <!-- Navigation Tabs -->
    <div class="border-b border-gray-200 bg-gray-50 -mx-6 px-6">
      <nav class="flex space-x-8">
        <button
          @click="selectedSection = 'details'"
          :class="[
            'py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
            selectedSection === 'details'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
        >
          Banner Details
        </button>
      </nav>
    </div>
    <!-- Content Section -->
    <div class="py-6" v-if="selectedSection === 'details'">
      <template v-if="!loadingSaving">
        <template v-if="bannerToUpdate">
          <!-- Banner Title -->
          <div class="mb-6">
            <label
              for="banner-name"
              class="block text-sm font-medium text-gray-700 mb-2"
            >
              Banner Title
            </label>
            <input
              id="banner-name"
              type="text"
              v-model="bannerToUpdate.title"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              placeholder="Enter banner title..."
            />
          </div>
          <!-- Banner Image -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Banner Image
            </label>

            <div class="flex justify-center">
              <template v-if="loadingImage">
                <div
                  class="flex items-center justify-center w-64 h-32 bg-gray-100 rounded-lg"
                >
                  <BaseSmallSpinner />
                </div>
              </template>
              <template v-else>
                <template v-if="bannerToUpdate.url != null">
                  <!-- Image Preview with Actions -->
                  <div class="relative group">
                    <img
                      :src="bannerToUpdate.url"
                      class="w-64 h-auto rounded-lg shadow-md border border-gray-200"
                      alt="Banner preview"
                    />

                    <!-- Action Buttons Overlay -->
                    <div
                      class="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    >
                      <a
                        :href="bannerToUpdate.url"
                        target="_blank"
                        class="w-8 h-8 bg-white hover:bg-gray-100 text-gray-700 rounded-md shadow-md flex items-center justify-center transition-all duration-200 hover:scale-105"
                        title="View Image"
                      >
                        <span class="text-xs">👁️</span>
                      </a>
                      <button
                        @click="showFileSelector = true"
                        class="w-8 h-8 bg-white hover:bg-gray-100 text-gray-700 rounded-md shadow-md flex items-center justify-center transition-all duration-200 hover:scale-105"
                        title="Change Image"
                      >
                        <span class="text-xs">✏️</span>
                      </button>
                      <button
                        @click="deleteBannerImage()"
                        v-if="bannerToUpdate.url === banner.url"
                        class="w-8 h-8 bg-white hover:bg-gray-100 text-gray-700 rounded-md shadow-md flex items-center justify-center transition-all duration-200 hover:scale-105"
                        title="Remove Image"
                      >
                        <span class="text-xs">🗑️</span>
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <!-- Upload Placeholder -->
                  <button
                    @click="showFileSelector = true"
                    class="w-64 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200 group"
                  >
                    <span
                      class="text-2xl mb-2 group-hover:scale-110 transition-transform duration-200"
                      >📤</span
                    >
                    <span class="text-sm font-medium">Upload Banner Image</span>
                    <span class="text-xs text-gray-400 mt-1"
                      >Click to browse files</span
                    >
                  </button>
                </template>
              </template>
            </div>
          </div>
          <!-- Banner Status -->
          <div class="mb-6">
            <label
              for="status"
              class="block text-sm font-medium text-gray-700 mb-2"
            >
              Status
            </label>
            <div class="flex items-center space-x-3">
              <BaseSwitchCustom id="status" v-model="bannerToUpdate.status" />
              <span class="text-sm text-gray-600">
                {{ bannerToUpdate.status === "1" ? "Active" : "Inactive" }}
              </span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div
            v-if="changesPending"
            class="flex justify-end space-x-3 pt-4 border-t border-gray-200"
          >
            <button
              @click="discardChanges()"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Discard Changes
            </button>
            <button
              @click="confirmChanges()"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
            >
              {{ banner.id ? "Update Banner" : "Create Banner" }}
            </button>
          </div>
        </template>
      </template>
      <template v-else>
        <!-- Loading State -->
        <div class="flex items-center justify-center py-12">
          <div class="flex flex-col items-center space-y-3">
            <BaseSmallSpinner />
            <p class="text-sm text-gray-500">Loading banner details...</p>
          </div>
        </div>
      </template>
    </div>
  </BaseModal>
</template>
<script>
import FileSelector from "@/components/FileSelector.vue";
import { createBanner, updateBanner } from "@/api/store/BannersService.js";

export default {
  components: {
    FileSelector
  },
  data() {
    return {
      isOpenModal: true,
      bannerToUpdate: null,
      selectedSection: "details",
      loadingImage: false,
      showFileSelector: false,
      subCategories: [],
      loadingSaving: false
    };
  },
  props: {
    banner: {
      type: Object,
      required: true
    }
  },
  computed: {
    changesPending() {
      return !(
        JSON.stringify(this.bannerToUpdate) == JSON.stringify(this.banner)
      );
    }
  },
  watch: {
    isOpenModal(val) {
      if (!val) {
        this.$emit("closedBannerEdition");
      } else {
        this.subCategories = [];
      }
    },
    banner(val) {
      this.bannerToUpdate = { ...val };
    },
    selectedSection(val) {
      if (val == "subcategories") {
        this.loadSubcategories();
      }
    }
  },
  mounted() {
    this.bannerToUpdate = { ...this.banner };
  },
  methods: {
    createdSubbanner(subCategory) {
      this.subCategories.unshift(subCategory);
    },
    updatedSubbanner(subCategory) {
      const i = this.subCategories.findIndex(x => {
        return x.id == subCategory.id;
      });
      this.subCategories[i] = { ...subCategory };
    },
    async deleteBannerImage() {
      this.bannerToUpdate.url = null;
    },
    fileUploaded(file) {
      this.bannerToUpdate.url = file.s3Url;
    },
    discardChanges() {
      this.bannerToUpdate = { ...this.banner };
    },
    async confirmChanges() {
      if (this.banner.id == null) {
        let res = await createBanner(this.bannerToUpdate);
        if (res.status == "success") {
          this.$emit("createdBanner", res.banner);
          let message = "banner created";
          this.$toast.open({
            message: message,
            type: "success",
            position: "top-right"
          });
        } else {
          this.$toast.open({
            message: `Error creating banner`,
            type: "error",
            position: "top-right"
          });
        }
      } else {
        let res = await updateBanner(this.banner.id, this.bannerToUpdate);
        if (res.status == "success") {
          this.$emit("updatedBanner", res.banner);
          let message = "banner updated";
          this.$toast.open({
            message: message,
            type: "success",
            position: "top-right"
          });
        } else {
          this.$toast.open({
            message: `Error updating banner`,
            type: "error",
            position: "top-right"
          });
        }
      }
    },
    subbannerDeleted(subbanner) {
      this.subCategories = this.subCategories.filter(x => {
        return x.id != subbanner.id;
      });
    }
  }
};
</script>
<style lang="scss" scoped>
/* Custom styles for modal override if needed */
:deep(.order-modal) {
  .modal-content {
    max-width: 600px;
  }
}

/* Ensure Tailwind classes work properly */
.banner-image {
  max-width: 100%;
  height: auto;
  object-fit: cover;
}

/* Custom scrollbar for content areas */
.content-area {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

.content-area::-webkit-scrollbar {
  width: 6px;
}

.content-area::-webkit-scrollbar-track {
  background: #f7fafc;
  border-radius: 3px;
}

.content-area::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.content-area::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>
